'use client';

import { useEffect, useRef } from 'react';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { capturePostHogEvent } from '~/lib/posthog-capture';

type PulseType = 'start' | 'heartbeat' | 'end';

const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Tracks authenticated app sessions for the founder dashboard.
 *
 * Writes to user_analytics via /api/session/pulse (first-party source of truth)
 * and mirrors start/end to PostHog for product analytics.
 *
 * Why not read session length from PostHog?
 * - Browser project tokens are write-only.
 * - Querying PostHog needs a personal API key + HogQL and is not real-time.
 * - Founder metrics need durable rows on user_analytics.
 */
export function SessionTracker() {
  const supabase = useSupabase();
  const activeUserId = useRef<string | null>(null);
  const startedForUser = useRef<string | null>(null);

  useEffect(() => {
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const pulse = async (type: PulseType, userId: string) => {
      try {
        const response = await fetch('/api/session/pulse', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type }),
          // Survive tab close for end/heartbeat flushes.
          keepalive: true,
          credentials: 'same-origin',
        });

        if (!response.ok) {
          return;
        }

        const result = (await response.json()) as {
          sessionStarted?: boolean;
          sessionEnded?: boolean;
          totalSessions?: number;
          totalTimeSeconds?: number;
          secondsAdded?: number;
        };

        if (type === 'start' || result.sessionStarted) {
          capturePostHogEvent('session_started', {
            user_id: userId,
            total_sessions: result.totalSessions,
          });
        }

        if (type === 'end' || result.sessionEnded) {
          capturePostHogEvent('session_ended', {
            user_id: userId,
            total_sessions: result.totalSessions,
            total_time_seconds: result.totalTimeSeconds,
            seconds_added: result.secondsAdded,
          });
        }
      } catch {
        // Best-effort; never block the UI on analytics.
      }
    };

    const startHeartbeat = (userId: string) => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
      }

      heartbeatTimer = setInterval(() => {
        if (document.visibilityState === 'visible') {
          void pulse('heartbeat', userId);
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    const onVisibility = () => {
      const userId = activeUserId.current;
      if (!userId) return;

      if (document.visibilityState === 'hidden') {
        void pulse('heartbeat', userId);
      } else if (document.visibilityState === 'visible') {
        void pulse('heartbeat', userId);
      }
    };

    const onPageHide = () => {
      const userId = activeUserId.current;
      if (!userId) return;
      void pulse('end', userId);
    };

    const bootstrap = async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;

      const userId = data.user?.id ?? null;
      activeUserId.current = userId;

      if (!userId) {
        startedForUser.current = null;
        return;
      }

      if (startedForUser.current !== userId) {
        startedForUser.current = userId;
        await pulse('start', userId);
      }

      startHeartbeat(userId);
    };

    void bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const userId = session?.user.id ?? null;
      activeUserId.current = userId;

      if (event === 'SIGNED_OUT') {
        if (startedForUser.current) {
          void pulse('end', startedForUser.current);
        }
        startedForUser.current = null;
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
          heartbeatTimer = null;
        }
        return;
      }

      if (
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
        userId &&
        startedForUser.current !== userId
      ) {
        startedForUser.current = userId;
        void pulse('start', userId);
        startHeartbeat(userId);
      }
    });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
      }
    };
  }, [supabase]);

  return null;
}
